import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import DirectionalText from "@/components/DirectionalText";
import { getTextDirection } from "@/lib/textDirection";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;

const REASON_ICONS = {
  "الإجابة الصحيحة غير صحيحة":        "❌",
  "يوجد خطأ إملائي أو لغوي":           "📝",
  "الصورة لا تطابق السؤال أو الإجابة": "🖼️",
  "السؤال غير واضح أو ملتبس":          "🤔",
  "المعلومة غير دقيقة أو قديمة":       "📚",
  "سؤال مكرر بشكل مزعج":              "🚫",
  "محتوى غير مناسب":                   "⚠️",
  "سبب آخر":                            "📌",
};

const NEEDS_REVIEW_THRESHOLD = 5;

function QuestionDetailModal({ questionId, token, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    load();
  }, [questionId]);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/reports/question/${questionId}`, { headers });
      setData(d);
      if (d.question) setEditForm({
        text: d.question.text,
        answer: d.question.answer,
        category_id: d.question.category_id,
        difficulty: d.question.difficulty,
        image_url: d.question.image_url || "",
        answer_image_url: d.question.answer_image_url || "",
        audio_url: d.question.audio_url || "",
        question_type: d.question.question_type || "text",
      });
    } catch { toast.error("خطأ في تحميل التفاصيل"); }
    setLoading(false);
  };

  const handleResolveAll = async () => {
    setActionLoading(true);
    try {
      await axios.put(`${API}/admin/reports/question/${questionId}/resolve-all`, {}, { headers });
      toast.success("تم حل جميع البلاغات ✅");
      load();
    } catch { toast.error("فشل في حل البلاغات"); }
    setActionLoading(false);
  };

  const handleToggleHidden = async () => {
    setActionLoading(true);
    try {
      const { data: r } = await axios.put(`${API}/admin/questions/${questionId}/toggle-hidden`, {}, { headers });
      toast.success(r.hidden ? "تم إخفاء السؤال مؤقتاً 🙈" : "تم إظهار السؤال ✅");
      load();
    } catch { toast.error("فشل في تغيير حالة الإخفاء"); }
    setActionLoading(false);
  };

  const handleSaveEdit = async () => {
    setActionLoading(true);
    try {
      await axios.put(`${API}/questions/${questionId}`, editForm, { headers });
      toast.success("تم حفظ التعديلات ✏️");
      setEditMode(false);
      load();
    } catch { toast.error("فشل حفظ التعديلات"); }
    setActionLoading(false);
  };

  const handleDelete = async () => {
    if (!window.confirm("هل أنت متأكد من حذف هذا السؤال نهائياً؟")) return;
    setActionLoading(true);
    try {
      await axios.delete(`${API}/admin/questions/${questionId}`, { headers });
      toast.success("تم حذف السؤال 🗑️");
      onClose(true);
    } catch { toast.error("فشل في حذف السؤال"); }
    setActionLoading(false);
  };

  const q = data?.question;
  const reports = data?.reports || [];
  const pendingCount = reports.filter(r => !r.resolved).length;

  return (
    <div
      style={{ position:"fixed", inset:0, zIndex:60, background:"rgba(0,0,0,0.55)", backdropFilter:"blur(4px)", display:"flex", alignItems:"center", justifyContent:"center", padding:"16px" }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl w-full overflow-y-auto"
        style={{ maxWidth:"680px", maxHeight:"90vh" }}
      >
        {loading ? (
          <div className="flex items-center justify-center h-64 text-primary/40 text-lg">⏳ جارٍ التحميل...</div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-primary/10">
              <div>
                <div className="text-lg font-black text-primary">تفاصيل السؤال والبلاغات</div>
                <div className="text-xs text-primary/40 mt-0.5 font-mono">{questionId}</div>
              </div>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-all font-black">✕</button>
            </div>

            {q ? (
              <div className="p-5 space-y-5">
                {/* Question card */}
                <div className={`rounded-2xl border p-4 ${q.is_hidden ? "bg-amber-50 border-amber-300" : "bg-primary/5 border-primary/10"}`}>
                  {q.is_hidden && (
                    <div className="text-xs font-black text-amber-700 bg-amber-100 px-3 py-1 rounded-full inline-block mb-3">🙈 مخفي مؤقتاً</div>
                  )}

                  {editMode ? (
                    <div className="space-y-3">
                      <textarea
                        value={editForm.text}
                        dir={getTextDirection(editForm.text)}
                        onChange={e => setEditForm(p => ({ ...p, text: e.target.value }))}
                        rows={3}
                        className="w-full border-2 border-primary/20 focus:border-primary rounded-xl px-3 py-2 text-sm outline-none resize-none font-bold text-primary"
                        style={{ unicodeBidi: "isolate" }}
                        placeholder="نص السؤال"
                      />
                      <input
                        value={editForm.answer}
                        onChange={e => setEditForm(p => ({ ...p, answer: e.target.value }))}
                        className="w-full border-2 border-primary/20 focus:border-primary rounded-xl px-3 py-2 text-sm outline-none font-bold text-primary"
                        placeholder="الإجابة"
                      />
                      <div className="flex gap-2">
                        <button onClick={handleSaveEdit} disabled={actionLoading} className="flex-1 bg-primary text-secondary py-2 rounded-xl text-sm font-black hover:opacity-90 disabled:opacity-40">
                          {actionLoading ? "⏳" : "✅ حفظ التعديلات"}
                        </button>
                        <button onClick={() => setEditMode(false)} className="px-4 bg-primary/10 text-primary py-2 rounded-xl text-sm font-bold">إلغاء</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <DirectionalText as="div" className="font-black text-primary text-base mb-2">{q.text}</DirectionalText>
                      <div className="text-sm text-primary/60">الإجابة: <span className="font-black text-primary">{q.answer}</span></div>
                      {q.image_url && (
                        <img src={q.image_url} alt="" className="mt-3 rounded-xl h-32 object-cover" onError={e => e.target.style.display = "none"} />
                      )}
                      <div className="flex flex-wrap gap-2 mt-3">
                        <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-lg font-bold">{q.category_id}</span>
                        <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-lg font-bold">{q.difficulty} نقطة</span>
                        {q.question_type && q.question_type !== "text" && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-lg font-bold">{q.question_type}</span>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Action buttons */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={handleResolveAll}
                    disabled={actionLoading || pendingCount === 0}
                    className="flex items-center justify-center gap-2 bg-green-50 text-green-700 border border-green-200 py-2.5 rounded-xl text-sm font-black hover:bg-green-100 transition-all disabled:opacity-40"
                  >
                    ✅ تم التحقق (إغلاق كل البلاغات)
                  </button>
                  <button
                    onClick={() => { setEditMode(true); setEditForm({ text: q.text, answer: q.answer, category_id: q.category_id, difficulty: q.difficulty, image_url: q.image_url || "", answer_image_url: q.answer_image_url || "", audio_url: q.audio_url || "", question_type: q.question_type || "text" }); }}
                    disabled={actionLoading || editMode}
                    className="flex items-center justify-center gap-2 bg-blue-50 text-blue-700 border border-blue-200 py-2.5 rounded-xl text-sm font-black hover:bg-blue-100 transition-all disabled:opacity-40"
                  >
                    ✏️ تعديل السؤال
                  </button>
                  <button
                    onClick={handleToggleHidden}
                    disabled={actionLoading}
                    className="flex items-center justify-center gap-2 bg-amber-50 text-amber-700 border border-amber-200 py-2.5 rounded-xl text-sm font-black hover:bg-amber-100 transition-all disabled:opacity-40"
                  >
                    {q.is_hidden ? "👁 إظهار السؤال" : "🙈 إخفاء مؤقتاً"}
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={actionLoading}
                    className="flex items-center justify-center gap-2 bg-red-50 text-red-700 border border-red-200 py-2.5 rounded-xl text-sm font-black hover:bg-red-100 transition-all disabled:opacity-40"
                  >
                    🗑️ حذف السؤال
                  </button>
                </div>

                {/* Reports list */}
                <div>
                  <div className="font-black text-primary mb-3">
                    البلاغات ({reports.length})
                    {pendingCount > 0 && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold mr-2">{pendingCount} معلّق</span>}
                  </div>
                  {reports.length === 0 ? (
                    <div className="text-center py-6 text-primary/30 text-sm">لا توجد بلاغات</div>
                  ) : (
                    <div className="space-y-2">
                      {reports.map(r => (
                        <div key={r.id} className={`rounded-xl p-3 border text-sm ${r.resolved ? "bg-green-50 border-green-100 opacity-60" : "bg-primary/5 border-primary/10"}`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span>{REASON_ICONS[r.reason] || "🚩"}</span>
                            <span className="font-bold text-primary">{r.reason}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${r.resolved ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                              {r.resolved ? "✓ محلول" : "معلّق"}
                            </span>
                            <span className="text-xs text-primary/30 mr-auto">{new Date(r.created_at).toLocaleDateString("ar-SA")}</span>
                          </div>
                          {r.details && <div className="text-xs text-primary/50 mt-1.5 bg-white rounded-lg px-2 py-1">{r.details}</div>}
                          <div className="text-xs text-primary/30 mt-1">المُبلِّغ: {r.reporter_name}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-10 text-center text-primary/40">
                <div className="text-4xl mb-3">🗑️</div>
                <div>السؤال محذوف أو غير موجود</div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function ReportsAdminPanel({ token }) {
  const [grouped, setGrouped] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [view, setView] = useState("grouped"); // grouped | all
  const [allReports, setAllReports] = useState([]);
  const [allTotal, setAllTotal] = useState(0);
  const [allSkip, setAllSkip] = useState(0);
  const [allFilter, setAllFilter] = useState("pending");
  const LIMIT = 30;
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadGrouped(); }, []);
  useEffect(() => {
    if (view === "all") loadAll();
  }, [view, allFilter, allSkip]);

  const loadGrouped = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reports/grouped`, { headers });
      setGrouped(data || []);
    } catch { toast.error("خطأ في تحميل البلاغات"); }
    setLoading(false);
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const resolved = allFilter === "pending" ? false : allFilter === "resolved" ? true : undefined;
      const params = new URLSearchParams({ limit: LIMIT, skip: allSkip });
      if (resolved !== undefined) params.append("resolved", resolved);
      const { data } = await axios.get(`${API}/admin/reports?${params}`, { headers });
      setAllReports(data.reports || []);
      setAllTotal(data.total || 0);
    } catch { toast.error("خطأ في تحميل البلاغات"); }
    setLoading(false);
  };

  const handleCloseDetail = (deleted) => {
    setSelectedQuestion(null);
    if (deleted) {
      loadGrouped();
    } else {
      loadGrouped();
    }
  };

  const handleResolveOne = async (reportId) => {
    try {
      await axios.put(`${API}/admin/reports/${reportId}/resolve`, {}, { headers });
      toast.success("تم تحديد البلاغ كمحلول");
      loadAll();
    } catch { toast.error("فشل تحديث البلاغ"); }
  };

  const totalPending = grouped.reduce((s, g) => s + g.pending, 0);
  const needsReview = grouped.filter(g => g.pending >= NEEDS_REVIEW_THRESHOLD);

  return (
    <div className="p-6">
      {/* Detail modal */}
      {selectedQuestion && (
        <QuestionDetailModal
          questionId={selectedQuestion}
          token={token}
          onClose={handleCloseDetail}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-black">🚩 البلاغات</h2>
          <p className="text-primary/50 text-sm mt-1">
            {grouped.length} سؤال مُبلَّغ عنه · {totalPending} بلاغ معلّق
            {needsReview.length > 0 && (
              <span className="mr-2 text-red-600 font-black">· 🚨 {needsReview.length} تحتاج مراجعة عاجلة</span>
            )}
          </p>
        </div>
        <button onClick={() => { loadGrouped(); if (view === "all") loadAll(); }}
          className="bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-bold hover:bg-primary/20 transition-all">
          ↻ تحديث
        </button>
      </div>

      {/* Needs review urgent banner */}
      {needsReview.length > 0 && (
        <div className="mb-5 bg-red-50 border border-red-300 rounded-2xl p-4">
          <div className="text-red-700 font-black text-sm mb-2">🚨 أسئلة تحتاج مراجعة عاجلة ({needsReview.length})</div>
          <div className="flex flex-wrap gap-2">
            {needsReview.map(g => (
              <button
                key={g.question_id}
                onClick={() => setSelectedQuestion(g.question_id)}
                className="text-xs bg-red-100 text-red-700 border border-red-300 px-3 py-1.5 rounded-xl font-bold hover:bg-red-200 transition-all"
              >
                <DirectionalText>{g.question_text?.slice(0, 40) || g.question_id}</DirectionalText>... ({g.pending} بلاغ)
              </button>
            ))}
          </div>
        </div>
      )}

      {/* View toggle */}
      <div className="flex gap-2 mb-6">
        {[
          { k: "grouped", l: "📋 مجمّع حسب السؤال" },
          { k: "all",     l: "📜 جميع البلاغات" },
        ].map(({ k, l }) => (
          <button key={k} onClick={() => setView(k)}
            className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${view === k ? "bg-primary text-secondary" : "bg-primary/10 text-primary hover:bg-primary/20"}`}>
            {l}
          </button>
        ))}
      </div>

      {/* ── GROUPED VIEW ── */}
      {view === "grouped" && (
        loading ? (
          <div className="text-center py-16 text-primary/40">⏳ جارٍ التحميل...</div>
        ) : grouped.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-5xl mb-3">✅</div>
            <div className="text-xl font-bold text-primary/40">لا توجد بلاغات</div>
          </div>
        ) : (
          <div className="space-y-3">
            {grouped.map(g => {
              const urgent = g.pending >= NEEDS_REVIEW_THRESHOLD;
              const reasonCounts = {};
              g.reasons.forEach(r => { reasonCounts[r] = (reasonCounts[r] || 0) + 1; });
              const topReasons = Object.entries(reasonCounts).sort((a, b) => b[1] - a[1]).slice(0, 3);

              return (
                <div
                  key={g.question_id}
                  className={`bg-white border rounded-2xl p-4 cursor-pointer hover:shadow-md transition-all ${urgent ? "border-red-300 ring-2 ring-red-200" : "border-primary/10"}`}
                  onClick={() => setSelectedQuestion(g.question_id)}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {urgent && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-black">🚨 يحتاج مراجعة</span>
                        )}
                        <span className="text-xs text-primary/40">{g.category_id}</span>
                        <span className="text-xs text-primary/30 mr-auto">
                          آخر بلاغ: {new Date(g.last_report).toLocaleDateString("ar-SA")}
                        </span>
                      </div>
                      <div className="font-black text-primary mb-2 line-clamp-2">
                        <DirectionalText>{g.question_text || "السؤال محذوف"}</DirectionalText>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {topReasons.map(([reason, count]) => (
                          <span key={reason} className="text-xs bg-primary/5 text-primary/70 px-2 py-0.5 rounded-lg font-bold">
                            {REASON_ICONS[reason] || "🚩"} {reason} {count > 1 ? `(${count})` : ""}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col items-center gap-1 flex-shrink-0">
                      <div className={`text-2xl font-black ${urgent ? "text-red-600" : "text-primary"}`}>{g.pending}</div>
                      <div className="text-xs text-primary/40 font-bold">معلّق</div>
                      {g.total > g.pending && (
                        <div className="text-xs text-green-600 font-bold">{g.total - g.pending} محلول</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ── ALL REPORTS VIEW ── */}
      {view === "all" && (
        <>
          <div className="flex gap-2 mb-4">
            {[
              ["pending", "معلّق", "amber"],
              ["resolved", "محلول", "green"],
              ["all", "الكل", "primary"],
            ].map(([k, l]) => (
              <button key={k} onClick={() => { setAllSkip(0); setAllFilter(k); }}
                className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${allFilter === k ? "bg-primary text-secondary" : "bg-primary/10 text-primary hover:bg-primary/20"}`}>
                {l}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-16 text-primary/40">⏳ جارٍ التحميل...</div>
          ) : allReports.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-5xl mb-3">✅</div>
              <div className="text-xl font-bold text-primary/40">لا توجد بلاغات</div>
            </div>
          ) : (
            <div className="space-y-3">
              {allReports.map(r => (
                <div key={r.id}
                  className={`bg-white border rounded-xl p-4 ${r.resolved ? "border-green-200 opacity-70" : "border-amber-200"}`}>
                  <div className="flex items-start gap-3">
                    <div className="text-2xl flex-shrink-0">{REASON_ICONS[r.reason] || "🚩"}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${r.resolved ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                          {r.resolved ? "✓ محلول" : "معلّق"}
                        </span>
                        <span className="text-xs text-primary/40 font-bold">{r.reason}</span>
                        <span className="text-xs text-primary/30">{new Date(r.created_at).toLocaleDateString("ar-SA", { year:"numeric", month:"short", day:"numeric" })}</span>
                      </div>
                      <DirectionalText as="div" className="font-bold text-primary text-sm mb-1 line-clamp-2">{r.question_text || "السؤال محذوف"}</DirectionalText>
                      {r.details && (
                        <div className="text-xs text-primary/50 bg-primary/5 rounded-lg px-3 py-2 mt-1">{r.details}</div>
                      )}
                      <div className="text-xs text-primary/40 mt-1">المُبلِّغ: {r.reporter_name}</div>
                    </div>
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <button onClick={() => setSelectedQuestion(r.question_id)}
                        className="text-xs bg-blue-100 text-blue-600 px-3 py-1.5 rounded-lg font-bold hover:bg-blue-200 transition-all">
                        عرض السؤال
                      </button>
                      {!r.resolved && (
                        <button onClick={() => handleResolveOne(r.id)}
                          className="text-xs bg-green-100 text-green-700 px-3 py-1.5 rounded-lg font-bold hover:bg-green-200 transition-all">
                          حل البلاغ
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {allTotal > LIMIT && (
            <div className="flex justify-center gap-3 mt-6">
              <button disabled={allSkip === 0} onClick={() => setAllSkip(Math.max(0, allSkip - LIMIT))}
                className="px-4 py-2 rounded-xl bg-primary/10 text-primary font-bold text-sm disabled:opacity-30">السابق</button>
              <span className="px-4 py-2 text-sm text-primary/50 font-bold">{Math.floor(allSkip/LIMIT)+1} / {Math.ceil(allTotal/LIMIT)}</span>
              <button disabled={allSkip + LIMIT >= allTotal} onClick={() => setAllSkip(allSkip + LIMIT)}
                className="px-4 py-2 rounded-xl bg-primary/10 text-primary font-bold text-sm disabled:opacity-30">التالي</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
